import { useEffect, useState } from 'react';
import { getStudentDisplayName } from '../../config/studentNameMapping';
import './DragonFlyby.css';

/**
 * An Ender Dragon that flies in, tells one child to focus, and waits.
 *
 * Deliberately NOT a reward. 刘泽霖 is obsessed with Minecraft and was promised
 * a dragon if he focuses; this is him being shown the thing so that he wants
 * it, and nothing about it is gated on getting an answer right.
 *
 * It shows on EVERY tutor panel and does not time out — the child taps 好的 to
 * send it away, and it is back the next time he opens the tab. That is the
 * point: a standing reminder rather than something he can wait out. Turning it
 * off is a flag change, not a timer.
 *
 * If it ever does become earned, the only safe trigger is `verdict === 'pass'`.
 * On 2026-08-20 he cleared four lessons of tutor questions in under five
 * minutes by failing twice on purpose and taking the reveal — paying that out
 * with a dragon would buy exactly the behaviour this is meant to replace.
 *
 * Served as the original GIF, deliberately, at 1.44MB. A 532KB animated WebP
 * conversion looked like free money and was not: GIF frame DISPOSAL does not
 * survive the round trip. The source clears the canvas before each frame
 * (disposal=2 on 99 of its 100 frames); every encoder available here produced
 * WebP frames that alpha-blend onto the previous one and never clear, so the
 * wings smeared a trail behind the dragon. Measured against the source frames,
 * mean per-pixel error was 100/255 for ffmpeg's encode and 20/255 for the best
 * alternative, where a merely-compressed encode should be low single digits.
 *
 * It costs nothing that matters: immutable on the CDN, fetched once, and only
 * for a student carrying the flag. It never touches the critical path.
 */

const GIF = 'https://videos.littlelambenglish.com/rewards/ender-dragon.gif';

/** Matches the dragon-leave keyframes — how long the exit gets before unmount. */
const EXIT_MS = 1500;

/** The sprite's own pixels. Kept on the <img> so a decode can never shift the page. */
const NATURAL_WIDTH = 400;
const NATURAL_HEIGHT = 408;

/**
 * What the dragon says. Rob's own words and Rob's own name, because the promise
 * was made to this child in person — it should sound like the person who made
 * it, not like the app.
 */
const MESSAGE = 'Rob says try to do well.';

/** The dismiss button. The only way out, and the only tappable thing in the layer. */
const DISMISS = '好的';

/**
 * `<name>, <message>` — or just the message.
 *
 * getStudentDisplayName returns the studentId itself when it has no override,
 * no cached Authing name and no fallback, and "6942ac206f69aadae4e5e090, Rob
 * says try to do well." is worse than carrying no name at all.
 */
function greeting(studentId, studentName, message) {
  const name = getStudentDisplayName(studentId, studentName);
  if (!name || name === studentId) return message;
  return `${name}, ${message}`;
}

export default function DragonFlyby({ enabled = false, studentId, studentName, message = MESSAGE }) {
  // 'here' from mount until tapped, then 'leaving' for the exit, then gone.
  const [phase, setPhase] = useState(null);

  useEffect(() => {
    setPhase(enabled ? 'here' : null);
  }, [enabled]);

  useEffect(() => {
    if (phase !== 'leaving') return undefined;
    const timer = setTimeout(() => setPhase(null), EXIT_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  if (!enabled || !phase) return null;

  return (
    // pointerEvents is inline as well as in the stylesheet on purpose: this is a
    // full-viewport fixed layer sitting over the mic button until it is tapped
    // away, and if the CSS ever failed to load it would swallow every tap.
    // NOT aria-hidden — the sprite is decorative but the sentence is not.
    <div className="dragon-flyby" style={{ pointerEvents: 'none' }}>
      <div className={`dragon-stage ${phase === 'leaving' ? 'is-leaving' : 'is-here'}`}>
        <p className="dragon-bubble">
          {greeting(studentId, studentName, message)}
          {/* Opts back in to pointer events for itself alone. Inline for the same
              reason the layer's is: the parent's `none` always applies, so if the
              stylesheet failed to load this button would be dead — and it is the
              only way to get the dragon off the screen. */}
          <button
            type="button"
            className="dragon-dismiss"
            style={{ pointerEvents: 'auto' }}
            onClick={() => setPhase('leaving')}
          >
            {DISMISS}
          </button>
        </p>
        <img src={GIF} alt="" width={NATURAL_WIDTH} height={NATURAL_HEIGHT} />
      </div>
    </div>
  );
}
